import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Lightbulb, Layers, ShieldCheck, ArrowRight } from 'lucide-react';
import { services, whyChooseUs } from '../data/mock';
import { useTheme } from '../context/ThemeContext';
import { CheckCircle, Zap, Target, Headphones } from 'lucide-react';

const iconMap = {
  Users,
  Lightbulb,
  Layers,
  ShieldCheck,
  CheckCircle,
  Zap,
  Target,
  HeadphonesIcon: Headphones
};

// Service images in order: IT Staffing Solutions, IT Consulting Services, Project Based Teams, Quality Assurance
const serviceImages = [
  'https://customer-assets.emergentagent.com/job_67218e68-4bb7-4a39-b578-c9fa413502c7/artifacts/9a9zodpo_image.png',
  'https://customer-assets.emergentagent.com/job_67218e68-4bb7-4a39-b578-c9fa413502c7/artifacts/6n3xk86j_image.png',
  'https://customer-assets.emergentagent.com/job_67218e68-4bb7-4a39-b578-c9fa413502c7/artifacts/nls6u4ay_image.png',
  'https://customer-assets.emergentagent.com/job_67218e68-4bb7-4a39-b578-c9fa413502c7/artifacts/lqt5s8da_image.png'
];

const ServicesSection = () => {
  const { theme } = useTheme();

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className={`relative py-16 sm:py-20 lg:py-24 transition-colors duration-300 ${
      theme === 'dark' ? 'bg-black' : 'bg-gray-50'
    }`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-14 lg:mb-16">
          <div className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 mb-4 sm:mb-6 border ${
            theme === 'dark' ? 'border-white/20 bg-white/5' : 'border-gray-200 bg-white'
          }`}>
            <span className={`text-xs sm:text-sm font-medium tracking-wide uppercase ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>Our Services</span>
          </div>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            What We Offer
          </h2>
          <p className={`text-base sm:text-lg max-w-[600px] mx-auto ${
            theme === 'dark' ? 'text-white/60' : 'text-gray-600'
          }`}>
            Comprehensive IT staffing and consulting solutions to meet your business needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-12 sm:mb-16">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <div
                key={service.id}
                className={`group overflow-hidden transition-all duration-300 ${
                  theme === 'dark' 
                    ? 'bg-[#0a0a0a] border border-white/10 hover:border-[#00FFD1]/40' 
                    : 'bg-white border border-gray-200 hover:border-[#00997A]/40 hover:shadow-lg'
                }`}
              >
                {/* Service Image */}
                <div className="relative h-40 sm:h-48 lg:h-56 overflow-hidden">
                  <img 
                    src={serviceImages[index]} 
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className={`absolute inset-0 ${
                    theme === 'dark' 
                      ? 'bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent' 
                      : 'bg-gradient-to-t from-white via-white/50 to-transparent'
                  }`} />
                </div>

                {/* Content */}
                <div className="p-5 sm:p-6 lg:p-8">
                  <div className="flex items-start gap-4 sm:gap-5">
                    <div className={`p-3 sm:p-4 transition-colors ${
                      theme === 'dark' 
                        ? 'bg-[#00FFD1]/10 group-hover:bg-[#00FFD1]/20' 
                        : 'bg-[#00997A]/10 group-hover:bg-[#00997A]/20'
                    }`}>
                      <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${
                        theme === 'dark' ? 'text-[#00FFD1]' : 'text-[#00997A]'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-lg sm:text-xl font-semibold mb-2 ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {service.title}
                      </h3>
                      <p className={`text-sm sm:text-base mb-3 ${
                        theme === 'dark' ? 'text-white/60' : 'text-gray-600'
                      }`}>
                        {service.description}
                      </p>
                      <p className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-[#00FFD1]' : 'text-[#00997A]'
                      }`}>
                        → {service.outcome}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Professions We Aid */}
        <div className={`mb-12 sm:mb-16 p-6 sm:p-8 lg:p-10 ${
          theme === 'dark' ? 'bg-[#0a0a0a] border border-white/10' : 'bg-white border border-gray-200'
        }`}>
          <div className="text-center mb-8">
            <h3 className={`text-xl sm:text-2xl font-bold mb-3 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Professions We Aid
            </h3>
            <p className={`text-sm sm:text-base max-w-[600px] mx-auto ${
              theme === 'dark' ? 'text-white/60' : 'text-gray-600'
            }`}>
              We specialize in placing top-tier talent across these key IT disciplines
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { title: "Software Developers", skills: "Java, .NET, Python, Frontend, Backend", icon: "💻" },
              { title: "Business Analysts", skills: "Requirements, Documentation, Stakeholder Management", icon: "📊" },
              { title: "QA & Automation Testers", skills: "Manual Testing, Selenium, Cypress, Performance", icon: "✅" },
              { title: "Data Analysts & Engineers", skills: "SQL, Python, ETL, Data Warehousing, BI Tools", icon: "📈" },
              { title: "DevOps & Cloud Engineers", skills: "AWS, Azure, GCP, CI/CD, Kubernetes, Docker", icon: "☁️" }
            ].map((profession, index) => (
              <div 
                key={index}
                className={`group p-5 text-center transition-all duration-300 ${
                  theme === 'dark' 
                    ? 'bg-black border border-white/10 hover:border-[#00FFD1]/40' 
                    : 'bg-gray-50 border border-gray-200 hover:border-[#00997A]/40 hover:shadow-md'
                }`}
              >
                <div className="text-3xl mb-3">{profession.icon}</div>
                <h4 className={`font-semibold text-sm sm:text-base mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {profession.title}
                </h4>
                <p className={`text-xs leading-relaxed ${
                  theme === 'dark' ? 'text-white/50' : 'text-gray-500'
                }`}>
                  {profession.skills}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Why Choose Us */}
        <div className={`p-6 sm:p-8 lg:p-10 ${
          theme === 'dark' ? 'bg-[#0a0a0a] border border-white/10' : 'bg-white border border-gray-200'
        }`}>
          <h3 className={`text-xl sm:text-2xl font-bold mb-6 sm:mb-8 text-center ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Why Choose RKS Technologies
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {whyChooseUs.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <div key={index} className="flex flex-col items-center text-center group">
                  <div className={`p-3 sm:p-4 mb-3 sm:mb-4 transition-colors ${
                    theme === 'dark' 
                      ? 'bg-[#00FFD1]/10 group-hover:bg-[#00FFD1]/20' 
                      : 'bg-[#00997A]/10 group-hover:bg-[#00997A]/20'
                  }`}>
                    <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${
                      theme === 'dark' ? 'text-[#00FFD1]' : 'text-[#00997A]'
                    }`} />
                  </div>
                  <p className={`text-xs sm:text-sm ${
                    theme === 'dark' ? 'text-white/70' : 'text-gray-600'
                  }`}>
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="mt-8 sm:mt-10 text-center">
            <Link
              to="/contact"
              className={`group inline-flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 font-medium text-base sm:text-lg transition-all duration-300 ${
                theme === 'dark'
                  ? 'bg-[#00FFD1] text-black hover:bg-[#00FFD1]/80'
                  : 'bg-[#00997A] text-white hover:bg-[#007A61]'
              }`}
            >
              Request a Consultation
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
